(function () {
  if (window.__STVideoSliderAnimationInit) return;
  window.__STVideoSliderAnimationInit = true;

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), wait);
    };
  }

  // ST 2026-10-25 (Wrike 4591208963): [data-load-visible] videos (snippets/st-video-load-visible) keep autoplay, but only
  // the ACTIVE slide loads, and only while the slider is visible (same as sportstech-feature-slider.js, Wrike 4591202137):
  // no prime/warm-up of the other slides; a slide that loses "active" or the slider leaving the screen stops its download.
  const loadVisible = (vm) => vm.hasAttribute('data-load-visible');

  class STVideoSliderAnimation extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;

      this.viewport = this.querySelector('[data-vsa-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-vsa-slide]'));
      this.prevBtn = this.querySelector('[data-vsa-prev]');
      this.nextBtn = this.querySelector('[data-vsa-next]');
      this.dots = Array.from(this.querySelectorAll('[data-vsa-dot]'));
      this.dotsNav = this.dots.length ? this.dots[0].closest('nav') : null;
      this.arrowsNav = this.querySelector('.st-vsa__nav');

      this.loop = this.hasAttribute('data-loop');
      this.dimInactive = this.hasAttribute('data-dim-inactive');
      this.autoAdvance = parseInt(this.getAttribute('data-auto-advance'), 10) || 0;
      this.activeIndex = -1;
      this.maxIndex = this.slides.length - 1;
      this._isProgrammaticScroll = false;
      this._inViewport = false;
      this._reconcileTimer = null;

      if (!this.viewport || this.slides.length === 0) return;
      this.viewport.scrollLeft = 0;
      this._recalcBounds();
      this._updateDots();
      requestAnimationFrame(() => { this._recalcBounds(); this._updateDots(); });
      this._onResize = debounce(() => { this._recalcBounds(); this._updateDots(); }, 150);
      window.addEventListener('resize', this._onResize);

      // ─── Arrow / Dot / Click-Nav ──────────────────────────
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.activeIndex - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.activeIndex + 1));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

      this.slides.forEach((slide, i) => {
        const toggle = slide.querySelector('[data-vsa-toggle]');
        if (toggle) toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleActiveVideo();
        });
        slide.addEventListener('click', (e) => {
          if (i === this.activeIndex) return;
          if (e.target.closest('a, button, [role="button"]')) return;
          this.go(i);
        });
        slide.addEventListener('keydown', (e) => {
          if (i === this.activeIndex) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.go(i);
          }
        });
      });

      // ─── iOS-Autoplay-Unlock ────────────────────────────
      this._videosPrimed = false;
      const primeVideos = () => {
        if (this._videosPrimed) return;
        this._videosPrimed = true;
        this.slides.forEach((s, idx) => {
          const vm = s.querySelector('video-media');
          if (!vm || (loadVisible(vm) && idx !== this.activeIndex)) return;
          try { void vm.player; } catch (e) {}
          const video = vm.querySelector('video');
          if (!video) return;
          video.muted = true;
          video.playsInline = true;
          try {
            const p = video.play();
            if (p && typeof p.then === 'function') {
              p.then(() => {
                if (idx !== this.activeIndex) video.pause();
              }).catch(() => {});
            }
          } catch (e) {}
        });
      };
      this.addEventListener('touchstart', primeVideos, { passive: true });
      this.addEventListener('click', primeVideos, { passive: true });

      // ─── Scroll-Reconcile: einzige Quelle für activeIndex ─
      const reconcile = () => {
        if (this._isProgrammaticScroll) return;
        if (this._pinEnabled && this._pinState === 'pinned') return; // Pin-Loop ist während 'pinned' alleinige Quelle
        if (this._isMobileViewport()) {
          const idx = this._mobileSettledIndex();
          this.go(idx, { instant: true, force: true });
          return;
        }
        const idx = this._closestIndex();
        if (idx !== this.activeIndex) this._setActive(idx);
      };
      this._settleReconcile = () => {
        clearTimeout(this._reconcileTimer);
        this._reconcileTimer = setTimeout(reconcile, 120);
      };
      this._onScroll = () => this._settleReconcile();
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      if ('onscrollend' in window) {
        this._onScrollEnd = reconcile;
        this.viewport.addEventListener('scrollend', this._onScrollEnd, { passive: true });
      }

      // ─── Video-Controls hartnäckig unterdrücken (iOS fallback) ─
      this._controlsAttrObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.type === 'attributes' && m.attributeName === 'controls' && m.target.hasAttribute('controls')) {
            m.target.removeAttribute('controls');
          }
        });
      });
      const killControls = (video) => {
        video.removeAttribute('controls');
        video.controls = false;
        try {
          video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
          video.disablePictureInPicture = true;
          video.disableRemotePlayback = true;
        } catch (e) {}
        this._controlsAttrObserver.observe(video, { attributes: true, attributeFilter: ['controls'] });
      };
      this._slideChildObservers = [];
      this.slides.forEach((s) => {
        s.querySelectorAll('video').forEach(killControls);
        const co = new MutationObserver((muts) => {
          muts.forEach((m) => m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.tagName === 'VIDEO') killControls(node);
            else if (node.querySelectorAll) node.querySelectorAll('video').forEach(killControls);
          }));
        });
        co.observe(s, { childList: true, subtree: true });
        this._slideChildObservers.push(co);
      });

      // ─── Viewport-Entry: pausiert beim Leave. Play wird NICHT hier gesteuert. ─
      this._onVisibilityChange = () => {
        if (document.hidden) this._pauseAll();
        else if (this._inViewport && this.activeIndex >= 0) this._playIndex(this.activeIndex);
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            this._inViewport = e.isIntersecting;
            if (!e.isIntersecting) {
              this._pauseAll();
              this._clearMobileAutoAdvance();
            } else if (this._isMobileViewport() && this.activeIndex >= 0) {
              this._playIndex(this.activeIndex);
              this._scheduleMobileAutoAdvance(this.activeIndex);
            } else {
              this._resumeVisible();
            }
          });
        }, { threshold: 0.25 });
        this._io.observe(this);
      } else {
        this._inViewport = true;
      }

      // ─── Auto-Advance (optional) ──────────────────────────
      if (this.autoAdvance > 0) {
        this._startTimer();
        this.addEventListener('mouseenter', () => this._stopTimer());
        this.addEventListener('mouseleave', () => this._startTimer());
        this.addEventListener('touchstart', () => this._stopTimer(), { passive: true });
        this.addEventListener('touchend', () => {
          clearTimeout(this._touchResumeTimer);
          this._touchResumeTimer = setTimeout(() => this._startTimer(), 3000);
        }, { passive: true });
      }

      // ─── Pin/Scrollytelling (optional) ────────────────────
      this._pinEnabled = false;
      this._initPinScroll();

      // Im Pin-Modus wird das erste Video erst beim tatsächlichen Pinnen gestartet.
      this._setActive(0, { skipPlay: this._pinEnabled });
      if (this._pinEnabled) this._pauseIndex(0);
    }

    disconnectedCallback() {
      this._stopTimer();
      this._clearMobileAutoAdvance();
      clearTimeout(this._touchResumeTimer);
      clearTimeout(this._reconcileTimer);
      if (this._io) this._io.disconnect();
      if (this._controlsAttrObserver) this._controlsAttrObserver.disconnect();
      if (this._slideChildObservers) this._slideChildObservers.forEach((co) => co.disconnect());
      if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this._onScroll && this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
      if (this._onScrollEnd && this.viewport) this.viewport.removeEventListener('scrollend', this._onScrollEnd);
      if (this._onViewportWheel && this.viewport) this.viewport.removeEventListener('wheel', this._onViewportWheel);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onPinScroll) window.removeEventListener('scroll', this._onPinScroll);
      if (this._onPinResize) window.removeEventListener('resize', this._onPinResize);
      if (this._pinResizeObserver) this._pinResizeObserver.disconnect();
      if (this._pinWrapper) this._pinWrapper.style.height = '';
      if (this._pinEnabled && this.viewport) this.viewport.style.scrollSnapType = this._prevScrollSnapType || '';
      if (this._pinEnabled) {
        this._clearScrubVisuals();
        this.removeAttribute('data-vsa-scrubbing');
      }
    }

    _initPinScroll() {
      const pinWrapper = this.closest('.st-vsa-pin');
      if (!pinWrapper) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Mobile never gets the sticky scroll-pin/scrollytelling reveal — just the plain
      // horizontal swipe carousel (reconcile()/_mobileSettledIndex() already drive that
      // path whenever _pinEnabled stays false). Desktop pin logic below is untouched.
      if (this._isMobileViewport()) return;
      if (this.slides.length <= 1) return;

      this._pinEnabled = true;
      this._pinWrapper = pinWrapper;
      this._stickyHost = this.closest('.section');
      this._pinState = 'before';
      this._pinProgressOffset = 0;

      this._prevScrollSnapType = this.viewport.style.scrollSnapType;
      this.viewport.style.scrollSnapType = 'none';
      this._onViewportWheel = (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          e.preventDefault();
        }
      };
      this.viewport.addEventListener('wheel', this._onViewportWheel, { passive: false });

      const n = this.slides.length;
      const SLIDE_SCROLL_VH = 1.7;
      const TAIL_SCROLL_VH = 1.7;
      const segmentBoundariesVh = [0];
      for (let i = 0; i < n - 1; i++) segmentBoundariesVh.push(segmentBoundariesVh[i] + SLIDE_SCROLL_VH);
      segmentBoundariesVh.push(segmentBoundariesVh[n - 1] + TAIL_SCROLL_VH);
      const totalVh = segmentBoundariesVh[n];
      // Segmentgrenzen als Progress-Fraktion (0..1) statt in vh, für die progress-basierte Logik unten.
      const boundaries = segmentBoundariesVh.map((v) => v / totalVh);
      this._pinBoundaries = boundaries; // exposed for _applyScrubVisuals()
      const HYSTERESIS = (SLIDE_SCROLL_VH * 0.15) / totalVh;
      const recalcLayout = () => {
        // Guard against resizing/rotating across the mobile breakpoint mid-session:
        // mobile never gets the pin effect, so bail out and drop the inline height
        // instead of forcing a large px value that CSS then has to fight with !important.
        if (this._isMobileViewport()) {
          pinWrapper.style.height = '';
          return;
        }
        let sectionHeight = this._stickyHost.getBoundingClientRect().height;
        if (typeof this._buffer === 'number') sectionHeight -= this._buffer;
        this._buffer = window.innerHeight * totalVh;
        pinWrapper.style.height = (Math.max(0, sectionHeight) + this._buffer) + 'px';
      };
      const topOffset = () => parseFloat(getComputedStyle(pinWrapper).getPropertyValue('--st-vsa-pin-top-offset')) || 0;
      const docTop = () => pinWrapper.getBoundingClientRect().top + window.scrollY;

      this._pinRecalibrate = (targetIndex) => {
        const scrollY = window.scrollY;
        const start = docTop() - topOffset();
        const rawProgress = (scrollY - start) / this._buffer;
        const center = (boundaries[targetIndex] + boundaries[targetIndex + 1]) / 2;
        this._pinProgressOffset = center - rawProgress;
      };

      this._pinRecalibrateToProgress = (targetProgress) => {
        const scrollY = window.scrollY;
        const start = docTop() - topOffset();
        const rawProgress = (scrollY - start) / this._buffer;
        this._pinProgressOffset = targetProgress - rawProgress;
      };

      const update = () => {
        if (this._isProgrammaticScroll) return;
        if (this._isMobileViewport()) return;
        const scrollY = window.scrollY;
        const start = docTop() - topOffset();
        const end = start + this._buffer;

        const nextState = scrollY < start ? 'before' : (scrollY > end ? 'after' : 'pinned');
        if (nextState !== this._pinState) {
          this._pinState = nextState;
          if (nextState === 'pinned') {
            if (this.activeIndex === 0) {
              this._pinRecalibrateToProgress(0);
            } else if (this.activeIndex === this.slides.length - 1) {
              this._pinRecalibrateToProgress(1);
            } else {
              this._pinRecalibrate(this.activeIndex);
            }
            this._playIndex(this.activeIndex);
            this.setAttribute('data-vsa-scrubbing', '');
          } else if (nextState === 'after') {
            this._pinProgressOffset = 0;
            const lastIdx = this.slides.length - 1;
            this.go(lastIdx, { instant: true, force: true });
          } else {

            this._pinProgressOffset = 0;
            this.go(0, { instant: true, force: true });
          }
        }

        if (nextState === 'pinned') {
          const rawProgress = (scrollY - start) / this._buffer;
          const progress = Math.max(0, Math.min(1, rawProgress + this._pinProgressOffset));
          this._applyScrubVisuals(progress);
          const maxScrollLeft = this.viewport.scrollWidth - this.viewport.clientWidth;
          const targetLeft = progress * maxScrollLeft;
          if (typeof this.viewport.scrollTo === 'function') {
            this.viewport.scrollTo({ left: targetLeft, behavior: 'instant' });
          } else {
            this.viewport.scrollLeft = targetLeft;
          }

          if (this._isMobileViewport()) {

            const idx = this._mobileSettledIndex();
            if (idx !== this.activeIndex) this._setActive(idx);
          } else {
            let index = this.activeIndex < 0 ? 0 : this.activeIndex;
            while (index < n - 1 && progress >= boundaries[index + 1] + HYSTERESIS) index++;
            while (index > 0 && progress <= boundaries[index] - HYSTERESIS) index--;
            if (index !== this.activeIndex) this._setActive(index);
          }
        }
      };

      recalcLayout();

      let ticking = false;
      this._onPinScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { update(); ticking = false; });
      };
      this._onPinResize = debounce(() => { recalcLayout(); }, 150);
      window.addEventListener('scroll', this._onPinScroll, { passive: true });
      window.addEventListener('resize', this._onPinResize);
      this._pinResizeObserver = new ResizeObserver(debounce(() => { recalcLayout(); }, 150));
      this._pinResizeObserver.observe(this._stickyHost);

      update();
    }

    // ─── Navigation ─────────────────────────────────────────
    go(newIndex, { instant = false, force = false } = {}) {
      const n = this.slides.length;
      if (n === 0) return;
      if (this.loop) newIndex = (newIndex + n) % n;
      else newIndex = Math.max(0, Math.min(n - 1, newIndex));

      if (newIndex === this.activeIndex && !force) return;

      const target = this.slides[newIndex];
      if (!target) return;
      this._isProgrammaticScroll = true;
      clearTimeout(this._unlockTimer);
      if (this._pinEnabled) {
        this._clearScrubVisuals();
        this.removeAttribute('data-vsa-scrubbing');
      }

      this._setActive(newIndex);
      if (this._pinEnabled && this._pinRecalibrate) {
        this._pinRecalibrate(newIndex);
      }

      const left = this._snapTargetFor(newIndex);
      if (typeof this.viewport.scrollTo === 'function') {
        this.viewport.scrollTo({ left, behavior: instant ? 'instant' : 'smooth' });
      } else {
        this.viewport.scrollLeft = left;
      }
      const unlock = () => {
        this._isProgrammaticScroll = false;
        clearTimeout(this._reconcileTimer);
        if (this._pinEnabled && this._pinState === 'pinned') this.setAttribute('data-vsa-scrubbing', '');
      };
      if ('onscrollend' in window) {
        const once = () => { this.viewport.removeEventListener('scrollend', once); unlock(); };
        this.viewport.addEventListener('scrollend', once, { passive: true });
        this._unlockTimer = setTimeout(unlock, instant ? 300 : 1200);
      } else {
        this._unlockTimer = setTimeout(unlock, instant ? 200 : 800);
      }
    }
    _snapTargetFor(index) {
      const n = this.slides.length;
      if (n === 0) return 0;
      const i = Math.max(0, Math.min(n - 1, index));
      if (i === 0) return 0;
      const slide = this.slides[i];
      if (!slide) return 0;
      if (i === n - 1) {
        const alignEnd = (getComputedStyle(slide).scrollSnapAlign || '').indexOf('end') !== -1;
        if (alignEnd) return this.viewport.scrollWidth - this.viewport.clientWidth;
      }
      return slide.offsetLeft;
    }
    _applyScrubVisuals(progress) {
      if (!this.dimInactive) return;
      const boundaries = this._pinBoundaries;
      if (!boundaries) return;
      const PLATEAU = 0.45; // fraction of the half-segment that stays fully active
      for (let i = 0; i < this.slides.length; i++) {
        const segStart = boundaries[i], segEnd = boundaries[i + 1];
        const center = (segStart + segEnd) / 2;
        const half = (segEnd - segStart) / 2 || 1;
        const dist = Math.min(1, Math.abs(progress - center) / half);
        let t;
        if (dist <= PLATEAU) {
          t = 1;
        } else {
          const easeIn = (dist - PLATEAU) / (1 - PLATEAU); // 0..1 across the ease zone
          t = 1 - easeIn * easeIn * (3 - 2 * easeIn); // smoothstep, eased 1 → 0
        }
        const slide = this.slides[i];
        slide.style.opacity = (0.6 + 0.4 * t).toFixed(3);
        slide.style.filter = `grayscale(${(1 - t).toFixed(3)}) brightness(${(0.95 + 0.05 * t).toFixed(3)})`;
      }
    }
    _clearScrubVisuals() {
      this.slides.forEach((s) => {
        s.style.opacity = '';
        s.style.filter = '';
      });
    }
    // ─── Mobile: full-viewport active card + 80%-crossed advance ──────────
    _isMobileViewport() {
      return window.matchMedia('(max-width: 767px)').matches;
    }
    _mobileSettledIndex() {
      const n = this.slides.length;
      let active = this.activeIndex < 0 ? 0 : Math.min(this.activeIndex, n - 1);
      const scrollLeft = this.viewport.scrollLeft;

      while (active < n - 1) {
        const s = this.slides[active];
        if (!s || !s.offsetWidth) break;
        const restPos = this._snapTargetFor(active);
        const forwardCrossed = (scrollLeft - restPos) / s.offsetWidth;
        if (forwardCrossed >= 0.8) active++; else break;
      }
      while (active > 0) {
        const prevSlide = this.slides[active - 1];
        if (!prevSlide || !prevSlide.offsetWidth) break;
        const prevRestPos = this._snapTargetFor(active - 1);
        const backwardCrossed = (prevRestPos + prevSlide.offsetWidth - scrollLeft) / prevSlide.offsetWidth;
        if (backwardCrossed >= 0.8) active--; else break;
      }
      return active;
    }
    _closestIndex() {
      const scrollLeft = this.viewport.scrollLeft;
      const TOL = 1.5;
      let best = 0, bestDist = Infinity;
      for (let i = 0; i < this.slides.length; i++) {
        const snapPos = this._snapTargetFor(i);
        const dist = Math.abs(snapPos - scrollLeft);
        if (dist < bestDist - TOL) {
          bestDist = dist;
          best = i;
        } else if (Math.abs(dist - bestDist) <= TOL && i === this.activeIndex) {
          best = i;
        }
      }
      return best;
    }
    _recalcBounds() {
      if (this._isMobileViewport()) {
        this.maxIndex = this.slides.length - 1;
        return;
      }
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const TOL = 2;
      // offsetLeft includes the viewport's left padding; the browser snaps to offsetLeft - scroll-padding-left.
      // Without this, a 5th card cut off by less than one card width left maxIndex at 0 and hid arrows + dots.
      const padL = parseFloat(getComputedStyle(this.viewport).scrollPaddingLeft) || 0;
      let last = 0;
      for (let i = 0; i < this.slides.length; i++) {
        if (this.slides[i].offsetLeft - padL <= maxScroll + TOL) last = i;
      }
      this.maxIndex = last;
    }
    _updateDots() {
      const showNav = this.maxIndex >= 1;
      [this.dotsNav, this.arrowsNav].forEach((el) => {
        if (!el) return;
        el.hidden = !showNav;
        el.style.display = showNav ? '' : 'none';
      });
      const current = this.activeIndex < 0 ? 0 : Math.min(this.activeIndex, this.maxIndex);
      this.dots.forEach((d, i) => {
        const hide = i > this.maxIndex;
        d.hidden = hide;
        d.style.display = hide ? 'none' : '';
        d.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }
    _setActive(index, opts) {
      opts = opts || {};
      const prev = this.activeIndex;
      this.activeIndex = index;
      this.slides.forEach((s, i) => s.toggleAttribute('data-active', i === index));
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === Math.min(index, this.maxIndex) ? 'true' : 'false'));
      if (this.prevBtn && !this.loop) this.prevBtn.toggleAttribute('disabled', index === 0);
      if (this.nextBtn && !this.loop) this.nextBtn.toggleAttribute('disabled', index === this.slides.length - 1);
      this.slides.forEach((s) => {
        const t = s.querySelector('[data-vsa-toggle]');
        if (t) t.removeAttribute('data-paused');
      });
      if (prev !== index) {
        this._pauseIndex(prev);
        if (!opts.skipPlay) this._playIndex(index);
        this._warmAdjacent();
        if (this._isMobileViewport()) this._scheduleMobileAutoAdvance(index);
      }
    }
    _scheduleMobileAutoAdvance(index) {
      this._clearMobileAutoAdvance();
      if (!this._isMobileViewport()) return;
      const slide = this.slides[index];
      if (!slide) return;
      const n = this.slides.length;
      const advance = () => {
        this._clearMobileAutoAdvance();
        if (this.activeIndex !== index) return; // user already swiped elsewhere
        if (!this._inViewport) return; // section scrolled off-screen
        if (!this.loop && index >= n - 1) return; // respect the existing loop setting at the end
        this.go(index + 1);
      };
      if (slide.hasAttribute('data-has-video')) {
        const vm = slide.querySelector('video-media');
        const video = vm && vm.querySelector('video');
        if (!video) return;
        this._mobileAdvanceVideoEl = video;
        this._mobileAdvanceHandler = advance;
        this._mobileAdvanceOriginalLoop = video.loop;
        video.loop = false;
        video.addEventListener('ended', advance, { once: true });
      } else {
        this._mobileAdvanceTimer = setTimeout(advance, 4000);
      }
    }
    _clearMobileAutoAdvance() {
      if (this._mobileAdvanceTimer) {
        clearTimeout(this._mobileAdvanceTimer);
        this._mobileAdvanceTimer = null;
      }
      if (this._mobileAdvanceVideoEl && this._mobileAdvanceHandler) {
        this._mobileAdvanceVideoEl.removeEventListener('ended', this._mobileAdvanceHandler);
        this._mobileAdvanceVideoEl.loop = this._mobileAdvanceOriginalLoop;
      }
      this._mobileAdvanceVideoEl = null;
      this._mobileAdvanceHandler = null;
    }
    _warmAdjacent() {
      const n = this.slides.length;
      const targets = this.loop
        ? [(this.activeIndex - 1 + n) % n, (this.activeIndex + 1) % n]
        : [this.activeIndex - 1, this.activeIndex + 1].filter(i => i >= 0 && i < n);
      targets.forEach((i) => {
        const vm = this.slides[i].querySelector('video-media');
        if (!vm || vm.playerProxy || loadVisible(vm)) return;
        try { void vm.player; } catch (e) {}
      });
    }
    _playIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      const vm = this.slides[index].querySelector('video-media');
      if (!vm) return;
      if (loadVisible(vm)) {
        // theme.js DeferredMedia may play it on its own (in-view) only while it is the active slide.
        vm.paused = false;
        if (!this._inViewport) {
          vm.setAttribute('autoplay', '');
          return;
        }
      }
      try {
        vm.setAttribute('autoplay', '');
        if (vm.player && typeof vm.player.play === 'function') vm.player.play();
      } catch (e) {}
    }
    _pauseIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      const vm = this.slides[index].querySelector('video-media');
      if (!vm) return;
      vm.removeAttribute('autoplay');
      if (loadVisible(vm)) {
        vm.paused = true; // keeps theme.js in-view logic from starting a non-active slide
        if (!vm.playerProxy) return; // never created → nothing loaded, nothing to pause
      }
      try {
        if (vm.player && typeof vm.player.pause === 'function') vm.player.pause();
        else {
          const v = vm.querySelector('video');
          if (v && !v.paused) v.pause();
        }
      } catch (e) {}
      if (loadVisible(vm)) vm.stopLoading?.();
    }
    // Load-when-visible (desktop): the slider (re)entered the screen → the active slide starts, unless the user paused it.
    _resumeVisible() {
      const slide = this.slides[this.activeIndex];
      const vm = slide?.querySelector('video-media');
      if (!vm || !loadVisible(vm)) return;
      if (slide.querySelector('[data-vsa-toggle][data-paused]')) return;
      this._playIndex(this.activeIndex);
    }
    _pauseAll() {
      for (let i = 0; i < this.slides.length; i++) this._pauseIndex(i);
    }
    _toggleActiveVideo() {
      const vm = this.slides[this.activeIndex]?.querySelector('video-media');
      const toggle = this.slides[this.activeIndex]?.querySelector('[data-vsa-toggle]');
      if (!vm) return;
      try {
        if (vm.hasAttribute('playing')) {
          if (vm.player && typeof vm.player.pause === 'function') vm.player.pause();
          if (toggle) {
            toggle.setAttribute('data-paused', '');
            const lbl = toggle.getAttribute('data-play-label');
            if (lbl) toggle.setAttribute('aria-label', lbl);
          }
        } else {
          if (vm.player && typeof vm.player.play === 'function') vm.player.play();
          if (toggle) {
            toggle.removeAttribute('data-paused');
            const lbl = toggle.getAttribute('data-pause-label');
            if (lbl) toggle.setAttribute('aria-label', lbl);
          }
        }
      } catch (e) {}
    }
    _startTimer() {
      if (!this.autoAdvance) return;
      this._stopTimer();
      this._timer = window.setInterval(() => this.go(this.activeIndex + 1), this.autoAdvance);
    }
    _stopTimer() {
      if (this._timer) { window.clearInterval(this._timer); this._timer = null; }
    }
  }

  if (!customElements.get('st-video-slider-animation')) {
    customElements.define('st-video-slider-animation', STVideoSliderAnimation);
  }
})();